import { Fragment, useEffect, useId, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import NavBadge from '@/components/ui/NavBadge'
import type { NavBadges } from '@/hooks/useNavBadges'
import { matchesPath } from './navTree'
import type { NavItem } from './PortalLayout'

/**
 * The portal menu: section captions, links, badges and expanding groups. One
 * implementation for the fixed sidebar and the phone drawer, which used to
 * carry a copy each. Lives in its own module so neither layout imports the
 * other at runtime.
 */
export default function SideNav({
  nav,
  badges,
  onNavigate,
  onLayoutChange,
}: {
  nav: NavItem[]
  badges: NavBadges
  /** Called when a link is followed; the drawer closes itself here. */
  onNavigate?: () => void
  /** Called after a group opens or closes, so the sidebar can re-measure its scroll hint. */
  onLayoutChange?: () => void
}) {
  // Hidden items stay routable; they are only dropped from the menu.
  const visible = nav.filter((item) => !item.hidden)

  return (
    <>
      {visible.map((item, i) => {
        // A caption renders whenever the section changes; unlabelled items
        // fall under the default "Menu" caption.
        const section = item.section ?? 'Menu'
        const prev = i === 0 ? null : (visible[i - 1].section ?? 'Menu')
        return (
          <Fragment key={item.to}>
            {section !== prev && <p className="mb-3 ml-2 mt-4 text-xs uppercase text-background/50">{section}</p>}
            {item.children?.length ? (
              <NavGroup item={item} badges={badges} onNavigate={onNavigate} onLayoutChange={onLayoutChange} />
            ) : (
              <NavItemLink item={item} badges={badges} onNavigate={onNavigate} />
            )}
          </Fragment>
        )
      })}
    </>
  )
}

function NavItemLink({
  item,
  badges,
  onNavigate,
  sub = false,
}: {
  item: NavItem
  badges: NavBadges
  onNavigate?: () => void
  sub?: boolean
}) {
  const { pathname } = useLocation()
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn('side-menu-link', sub && 'side-menu-sublink', (isActive || item.also?.test(pathname)) && 'side-menu-link--active')
      }
    >
      {item.icon}
      <span className="ml-3 overflow-hidden text-ellipsis whitespace-nowrap">{item.label}</span>
      {item.badge && <NavBadge count={badges[item.badge]} label={`${item.label} need attention`} />}
    </NavLink>
  )
}

/**
 * A parent with sub-items, the Icewall side-menu sub list. It is a button, not
 * a link: it starts open while the current page is one of its children and
 * toggles on click. Collapsed over the active child it takes the active style,
 * so the menu still says where you are.
 */
function NavGroup({
  item,
  badges,
  onNavigate,
  onLayoutChange,
}: {
  item: NavItem
  badges: NavBadges
  onNavigate?: () => void
  onLayoutChange?: () => void
}) {
  const { pathname } = useLocation()
  const children = (item.children ?? []).filter((child) => !child.hidden)
  const inside = children.some((child) => matchesPath(child, pathname))
  const [open, setOpen] = useState(inside)
  const panelId = useId()

  // Arriving on one of its pages from elsewhere opens the group; leaving never
  // closes one the admin opened by hand.
  useEffect(() => {
    if (inside) setOpen(true)
  }, [inside])

  useEffect(() => {
    onLayoutChange?.()
  }, [open, onLayoutChange])

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className={cn('side-menu-link w-full text-left', inside && !open && 'side-menu-link--active')}
      >
        {item.icon}
        <span className="ml-3 overflow-hidden text-ellipsis whitespace-nowrap">{item.label}</span>
        <ChevronDown className={cn('ml-auto size-4 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div id={panelId} className="side-menu-sub">
          {children.map((child) => (
            <NavItemLink key={child.to} item={child} badges={badges} onNavigate={onNavigate} sub />
          ))}
        </div>
      )}
    </div>
  )
}
